import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import { AGENT_URL } from '../config/env'
import type { FilterCondition } from '../utils/filterUtils'
import { QUERY_GC_TIME_MS, QUERY_STALE_TIME_MS } from '../config/constants'
import { callKeys } from '../lib/queryKeys'

export interface CallsCountResponse {
  count: number
}

/**
 * Encode filters as filter[field][operator]=value query params, matching the
 * encoding used by fetchCalls so the count applies the identical filters as the
 * rows query. Empty/blank filter values are skipped.
 */
export function buildCallsCountQueryString(
  filters?: FilterCondition[]
): string {
  const queryParams = new URLSearchParams()
  if (filters && filters.length > 0) {
    for (const filter of filters) {
      const value = filter.value.trim()
      if (!value) continue
      queryParams.append(`filter[${filter.field}][${filter.operator}]`, value)
    }
  }
  return queryParams.toString()
}

async function fetchCallsCount(
  token: string,
  filters?: FilterCondition[]
): Promise<CallsCountResponse> {
  const response = await fetch(
    `${AGENT_URL}/data/calls/count?${buildCallsCountQueryString(filters)}`,
    {
      headers: {
        Authorization: token,
      },
    }
  )

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = `Failed to fetch calls count: ${response.status}`
    try {
      const errorData = JSON.parse(errorText)
      errorMessage = errorData.error || errorMessage
    } catch {
      if (errorText) errorMessage = errorText
    }
    throw new Error(errorMessage)
  }

  return response.json()
}

/**
 * Hook to fetch the total number of call records matching the given filters.
 *
 * Decoupled from useCallsQuery so the paginated rows render immediately and are
 * never blocked on the count. The Call Logs footer shows "<loaded> of <count>".
 */
export function useCallsCountQuery(
  params: { filters?: FilterCondition[] } = {},
  options: { enabled?: boolean } = {}
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: callKeys.count(organization?.id as string, params.filters),
    enabled: !!organization?.id && (options.enabled ?? true),
    queryFn: async () => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchCallsCount(token, params.filters)
    },
    staleTime: QUERY_STALE_TIME_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}
